import { useEffect, useMemo, useRef, useState } from 'react';
import { answerQuestion, listQuestionsForAgent, sendMessageToAgent } from '../api';
import { PermissionCard } from '../components/PermissionCard';
import type { AgentState, OutboundMessage, PermissionRequest, Question } from '../types';
import { classNames, relativeTime, shortenSummary } from '../util';

interface Props {
  agents: AgentState[];
  questions: Question[];
  permissions: PermissionRequest[];
  onPermissionDecided: (id: string) => void;
}

export function ChatTab({ agents, questions, permissions, onPermissionDecided }: Props) {
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const [outbound, setOutbound] = useState<Record<string, OutboundMessage[]>>({});

  // Force re-render every second so countdown timers tick
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // All known agent_ids: union of registered agents + agents that have questions/permissions
  const knownAgentIds = useMemo(() => {
    const set = new Set<string>();
    for (const a of agents) set.add(a.agent_id);
    for (const q of questions) set.add(q.agent_id);
    for (const p of permissions) set.add(p.agent_id);
    return Array.from(set);
  }, [agents, questions, permissions]);

  function lastActivityFor(agentId: string): number {
    let max = 0;
    for (const q of questions) {
      if (q.agent_id !== agentId) continue;
      const t = new Date(q.created_at).getTime();
      if (t > max) max = t;
      if (q.answer?.answered_at) {
        const at = new Date(q.answer.answered_at).getTime();
        if (at > max) max = at;
      }
    }
    for (const p of permissions) {
      if (p.agent_id !== agentId) continue;
      const t = new Date(p.created_at).getTime();
      if (t > max) max = t;
    }
    for (const m of outbound[agentId] || []) {
      const t = new Date(m.created_at).getTime();
      if (t > max) max = t;
    }
    const a = agents.find((x) => x.agent_id === agentId);
    if (a) {
      const t = new Date(a.last_heartbeat).getTime();
      if (t > max) max = t;
    }
    return max;
  }

  const sortedAgentIds = useMemo(() => {
    return [...knownAgentIds].sort((a, b) => lastActivityFor(b) - lastActivityFor(a));
  }, [knownAgentIds, questions, permissions, outbound, agents]);

  const openCount = (agentId: string) =>
    questions.filter((q) => q.agent_id === agentId && q.status === 'open').length +
    permissions.filter((p) => p.agent_id === agentId && p.status === 'open').length;

  if (activeAgentId) {
    return (
      <ChatThread
        agentId={activeAgentId}
        agent={agents.find((a) => a.agent_id === activeAgentId)}
        liveQuestions={questions.filter((q) => q.agent_id === activeAgentId)}
        permissions={permissions.filter((p) => p.agent_id === activeAgentId)}
        outbound={outbound[activeAgentId] || []}
        onPermissionDecided={onPermissionDecided}
        onAppendOutbound={(msg) =>
          setOutbound((prev) => ({
            ...prev,
            [activeAgentId]: [...(prev[activeAgentId] || []), msg],
          }))
        }
        onUpdateOutbound={(id, patch) =>
          setOutbound((prev) => ({
            ...prev,
            [activeAgentId]: (prev[activeAgentId] || []).map((m) =>
              m.id === id ? { ...m, ...patch } : m
            ),
          }))
        }
        onBack={() => setActiveAgentId(null)}
      />
    );
  }

  return (
    <div className="tab chat">
      <h1 className="tab-title">Chat</h1>
      {sortedAgentIds.length === 0 && (
        <div className="muted">No agents have reached out yet.</div>
      )}
      <ul className="chat-agent-list">
        {sortedAgentIds.map((agentId) => {
          const lastQ = [...questions]
            .filter((q) => q.agent_id === agentId)
            .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
          const preview = lastQ
            ? shortenSummary(lastQ.question, 70)
            : 'No messages yet';
          const unread = openCount(agentId);
          const lastActivity = lastActivityFor(agentId);
          return (
            <li key={agentId}>
              <button
                className={classNames('chat-agent-card', unread > 0 && 'has-unread')}
                onClick={() => setActiveAgentId(agentId)}
              >
                <div className="chat-agent-card-row">
                  <span className="chat-agent-name">{agentId}</span>
                  <span className="chat-agent-time">
                    {lastActivity ? relativeTime(new Date(lastActivity).toISOString()) : ''}
                  </span>
                </div>
                <div className="chat-agent-card-row">
                  <span className="chat-agent-preview">{preview}</span>
                  {unread > 0 && <span className="chat-agent-badge">{unread}</span>}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface ThreadProps {
  agentId: string;
  agent?: AgentState;
  liveQuestions: Question[];
  permissions: PermissionRequest[];
  outbound: OutboundMessage[];
  onPermissionDecided: (id: string) => void;
  onAppendOutbound: (msg: OutboundMessage) => void;
  onUpdateOutbound: (id: string, patch: Partial<OutboundMessage>) => void;
  onBack: () => void;
}

type ThreadItem =
  | { kind: 'question'; data: Question; ts: number }
  | { kind: 'answer'; data: Question; ts: number }
  | { kind: 'outbound'; data: OutboundMessage; ts: number }
  | { kind: 'permission'; data: PermissionRequest; ts: number };

function ChatThread({
  agentId,
  agent,
  liveQuestions,
  permissions,
  outbound,
  onPermissionDecided,
  onAppendOutbound,
  onUpdateOutbound,
  onBack,
}: ThreadProps) {
  const [historyQuestions, setHistoryQuestions] = useState<Question[]>([]);
  const [historySupported, setHistorySupported] = useState<boolean | null>(null);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Load historical questions for this agent
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { questions, supported } = await listQuestionsForAgent(agentId);
        if (cancelled) return;
        setHistoryQuestions(questions);
        setHistorySupported(supported);
      } catch (e) {
        console.warn('history load failed', e);
        if (!cancelled) setHistorySupported(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // Merge live + history questions (live wins on collision)
  const mergedQuestions = useMemo(() => {
    const map = new Map<string, Question>();
    for (const q of historyQuestions) map.set(q.id, q);
    for (const q of liveQuestions) map.set(q.id, q);
    return Array.from(map.values());
  }, [historyQuestions, liveQuestions]);

  // Build a flat, time-sorted thread of items (oldest → newest)
  const items: ThreadItem[] = useMemo(() => {
    const out: ThreadItem[] = [];
    for (const q of mergedQuestions) {
      out.push({ kind: 'question', data: q, ts: new Date(q.created_at).getTime() });
      if (q.answer?.answered_at) {
        out.push({ kind: 'answer', data: q, ts: new Date(q.answer.answered_at).getTime() });
      }
    }
    for (const p of permissions) {
      out.push({ kind: 'permission', data: p, ts: new Date(p.created_at).getTime() });
    }
    for (const m of outbound) {
      out.push({ kind: 'outbound', data: m, ts: new Date(m.created_at).getTime() });
    }
    return out.sort((a, b) => a.ts - b.ts);
  }, [mergedQuestions, permissions, outbound]);

  const openQuestion = liveQuestions.find((q) => q.status === 'open');

  // Auto-scroll to bottom on new items
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [items.length]);

  async function send() {
    const text = composer.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const localMsg: OutboundMessage = {
      id: localId,
      agent_id: agentId,
      text,
      created_at: new Date().toISOString(),
      status: 'sending',
    };
    onAppendOutbound(localMsg);
    setComposer('');
    try {
      const r = await sendMessageToAgent(agentId, text);
      onUpdateOutbound(localId, {
        id: r.id || localId,
        status: r.backend_pending ? 'queued_pending_backend' : 'sent',
      });
    } catch (e) {
      onUpdateOutbound(localId, { status: 'failed', error: (e as Error).message });
      setSendError((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="tab chat-thread">
      <header className="chat-thread-head">
        <button className="chat-back" onClick={onBack} aria-label="Back to chat list">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="chat-thread-id">
          <div className="chat-thread-name">{agentId}</div>
          <div className="chat-thread-status">
            {agent ? agent.status_text || 'idle' : 'no heartbeat'}
          </div>
        </div>
      </header>

      <div className="chat-thread-scroll" ref={scrollRef}>
        {items.length === 0 && (
          <div className="muted chat-thread-empty">No conversation yet.</div>
        )}
        {historySupported === false && (
          <div className="chat-thread-note">
            Showing live questions only — backend has no history endpoint yet.
          </div>
        )}
        {items.map((item) => (
          <ThreadItemView
            key={threadKey(item)}
            item={item}
            isOpenLive={item.kind === 'question' && (item.data as Question).id === openQuestion?.id}
            onPermissionDecided={onPermissionDecided}
          />
        ))}
      </div>

      {/* Pinned open-question reply UI (tap-buttons or text input) */}
      {openQuestion && (
        <OpenQuestionReply question={openQuestion} />
      )}

      {/* Composer for unsolicited Steve → agent messages */}
      <div className="chat-composer">
        {sendError && <div className="chat-composer-error">{sendError}</div>}
        <div className="chat-composer-row">
          <textarea
            className="chat-composer-input"
            placeholder={`Message ${agentId}…`}
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            className="chat-composer-send"
            onClick={send}
            disabled={sending || !composer.trim()}
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12l16-8-6 16-3-7-7-1z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function threadKey(item: ThreadItem): string {
  switch (item.kind) {
    case 'question':
      return `q-${(item.data as Question).id}`;
    case 'answer':
      return `a-${(item.data as Question).id}`;
    case 'permission':
      return `p-${(item.data as PermissionRequest).id}`;
    case 'outbound':
      return `o-${(item.data as OutboundMessage).id}`;
  }
}

interface ItemViewProps {
  item: ThreadItem;
  isOpenLive: boolean;
  onPermissionDecided: (id: string) => void;
}

function ThreadItemView({ item, isOpenLive, onPermissionDecided }: ItemViewProps) {
  if (item.kind === 'question') {
    const q = item.data as Question;
    return (
      <div className="bubble bubble-left">
        <div className="bubble-text">{q.question}</div>
        {q.context_md && (
          <details className="bubble-context">
            <summary>context</summary>
            <pre>{q.context_md}</pre>
          </details>
        )}
        <div className="bubble-meta">
          <span>{relativeTime(q.created_at)}</span>
          {isOpenLive && <span className="bubble-status is-open">awaiting reply</span>}
          {q.status === 'defaulted' && <span className="bubble-status is-defaulted">defaulted</span>}
          {q.status === 'cancelled' && <span className="bubble-status is-cancelled">cancelled</span>}
        </div>
      </div>
    );
  }
  if (item.kind === 'answer') {
    const q = item.data as Question;
    const ans = q.answer!;
    const text = ans.text || labelForOption(q, ans.chose_option) || '—';
    return (
      <div className={classNames('bubble bubble-right', ans.answered_by === 'default' && 'is-default')}>
        <div className="bubble-text">{text}</div>
        <div className="bubble-meta">
          <span>{relativeTime(ans.answered_at)}</span>
          <span className="bubble-status is-sent">
            {ans.answered_by === 'default' ? 'auto-default' : 'sent'}
          </span>
        </div>
      </div>
    );
  }
  if (item.kind === 'outbound') {
    const m = item.data as OutboundMessage;
    return (
      <div className={classNames('bubble bubble-right', m.status === 'failed' && 'is-failed')}>
        <div className="bubble-text">{m.text}</div>
        <div className="bubble-meta">
          <span>{relativeTime(m.created_at)}</span>
          <span className={`bubble-status is-${m.status}`}>{statusLabel(m.status)}</span>
        </div>
      </div>
    );
  }
  // permission
  const p = item.data as PermissionRequest;
  return (
    <div className="bubble bubble-perm">
      <PermissionCard request={p} onDecided={() => onPermissionDecided(p.id)} />
    </div>
  );
}

function statusLabel(s: OutboundMessage['status']): string {
  switch (s) {
    case 'sending':
      return 'sending…';
    case 'sent':
      return 'queued for agent';
    case 'queued_pending_backend':
      return 'queued (backend pending)';
    case 'failed':
      return 'failed';
  }
}

function labelForOption(q: Question, optId?: string | null): string | null {
  if (!optId || !q.options) return null;
  const o = q.options.find((x) => x.id === optId);
  return o ? o.label : null;
}

// Lightweight reply UI for the currently-open question.
// Renders tap buttons or a text input — same UX as the old QuestionCard, just
// pinned at the bottom of the thread.
function OpenQuestionReply({ question }: { question: Question }) {
  const [submitting, setSubmitting] = useState(false);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const remainingSec = Math.max(
    0,
    Math.ceil(
      question.default_after_sec - (Date.now() - new Date(question.created_at).getTime()) / 1000
    )
  );

  async function pick(optionId: string) {
    if (submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await answerQuestion(question.id, { chose_option: optionId });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function sendText() {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await answerQuestion(question.id, { text: text.trim() });
      setText('');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="chat-open-q">
      <div className="chat-open-q-head">
        <span>Reply</span>
        <span className={classNames('q-timer', remainingSec <= 15 && 'is-low')}>
          {fmtTimer(remainingSec)}
        </span>
      </div>
      {question.options && question.options.length > 0 ? (
        <div className="q-options">
          {question.options.map((opt) => {
            const isDefault = opt.id === question.default_choice || opt.is_default;
            return (
              <button
                key={opt.id}
                className={classNames('q-option', isDefault && 'is-default')}
                onClick={() => pick(opt.id)}
                disabled={submitting}
              >
                {opt.label}
                {isDefault && <span className="q-option-tag">default</span>}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="q-text-input">
          <textarea
            placeholder="Your answer…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            disabled={submitting}
          />
          <button className="btn btn-primary" onClick={sendText} disabled={submitting || !text.trim()}>
            Send
          </button>
        </div>
      )}
      {err && <div className="q-error">{err}</div>}
    </div>
  );
}

function fmtTimer(sec: number) {
  if (sec >= 60) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  return `${sec}s`;
}
